import { useState } from "react";
import { Edit3, Plus, PowerOff, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { useCombos } from "@/hooks/useCombos";
import ComboDialog from "@/components/admin/ComboDialog";

/**
 * Menu combos, from /api/combos.
 *
 * The backend has been complete since migration 038 and had no screen: a combo could
 * be added to a bill and edited over the API, but not defined anywhere in the app.
 *
 * Writes are admin-only on the server. The check here only hides controls that would
 * 403 — it is convenience, not the gate.
 */
export default function ComboPanel({ token, menu, categories, isAdmin }) {
  const { combos, error, loading, createCombo, replaceComponents, deactivateCombo, fetchCombo } =
    useCombos(token);
  const [dialog, setDialog] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [working, setWorking] = useState(false);

  // A combo cannot contain another combo, so every combo id is off the picker.
  const comboIds = new Set(combos.map(combo => String(combo.id)));

  const openEdit = async combo => {
    // Re-read: PUT /components replaces the list wholesale, so a stale row would drop
    // whatever another terminal added while this page sat open.
    const result = await fetchCombo(combo.id);
    if (!result.ok) return toast.error(result.error);
    setDialog({ combo: result.data });
  };

  const confirmDeactivate = async () => {
    setWorking(true);
    const result = await deactivateCombo(confirming.id);
    setWorking(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(`${confirming.name} deactivated`);
    setConfirming(null);
  };

  return <section className="admin-table-panel combo-panel" data-testid="combo-management-panel">
    <div className="table-toolbar">
      <div>
        <h3>Combos</h3>
        <p>{combos.length} combo{combos.length === 1 ? "" : "s"} · one menu line, priced together</p>
      </div>
      {isAdmin && (
        <Button variant="action" onClick={() => setDialog({ combo: null })} data-testid="add-combo-button"><Plus/>New combo</Button>
      )}
    </div>

    {error && (
      <div className="kds-error" role="alert" data-testid="combo-panel-error">
        <TriangleAlert/>
        <span>{error}</span>
      </div>
    )}

    {combos.map(combo => (
      <div className={`staff-row ${combo.is_available ? "" : "inactive"}`} key={combo.id} data-testid={`combo-row-${combo.id}`}>
        <span>{combo.short_name || combo.name.slice(0, 2).toUpperCase()}</span>
        <div>
          <strong>{combo.name}</strong>
          <small>{combo.components.map(c => `${c.quantity}× ${c.name}`).join(" + ") || "No components"}</small>
        </div>
        <b>₹{combo.price}</b>
        {/* Negative savings means the combo costs more than its parts — a pricing
            mistake, so it is stated rather than dressed up as a saving. */}
        <i className={combo.savings >= 0 ? "" : "negative"} data-testid={`combo-row-${combo.id}-savings`}>
          {combo.savings >= 0 ? `saves ₹${combo.savings}` : `₹${Math.abs(combo.savings)} over`}
        </i>
        {isAdmin && (
          <button onClick={() => openEdit(combo)} aria-label={`Edit ${combo.name} components`} data-testid={`edit-combo-${combo.id}-button`}><Edit3/></button>
        )}
        {isAdmin && combo.is_available && (
          <button onClick={() => setConfirming(combo)} aria-label={`Deactivate ${combo.name}`} data-testid={`deactivate-combo-${combo.id}-button`}><PowerOff/></button>
        )}
      </div>
    ))}

    {!combos.length && !loading && !error && (
      <p className="chart-empty" data-testid="combo-panel-empty">No combos yet.</p>
    )}

    {dialog && (
      <ComboDialog
        combo={dialog.combo}
        menu={menu}
        categories={categories}
        excludeIds={comboIds}
        onCreate={createCombo}
        onReplace={replaceComponents}
        onClose={() => setDialog(null)}
      />
    )}

    {confirming && (
      <Modal title={`Deactivate ${confirming.name}?`} onClose={() => setConfirming(null)} testId="deactivate-combo-modal">
        <div className="workflow-form">
          {/* The endpoint is a soft delete and the wording has to match it: bills that
              already sold this combo point at the same menu item row. */}
          <p className="hint">
            It comes off the menu and can no longer be added to an order. Nothing is deleted —
            past bills that sold it are untouched, and its components stay as they are.
          </p>
          <Button variant="destructive" onClick={confirmDeactivate} disabled={working} data-testid="confirm-deactivate-combo-button">
            Deactivate combo
          </Button>
          <Button variant="outline" onClick={() => setConfirming(null)} data-testid="cancel-deactivate-combo-button">
            Keep it on the menu
          </Button>
        </div>
      </Modal>
    )}
  </section>;
}
